// LMS admin API calls. Base URL comes from .env (REACT_APP_HOST / REACT_APP_HOST_STAGING)
// and the admin login token is attached by the axios interceptor.
import axiosInstance from "../../config/axios_interceptor";
import { API_URL } from "../../config/global_constants";

const LMS_ADMIN = API_URL + "lms/admin/";
const LMS_STAFF = API_URL + "lms/staff/";
const FALLBACK_ERROR = "Something went wrong, please try again";

// Returns the whole LMS response ({ success, message, data }), or throws an Error with the API message.
// Validation errors ({ field: ["message"] }) are on error.errors.
const send = async (config) => {
  try {
    const { data } = await axiosInstance(config);
    if (!data?.success) throw Object.assign(new Error(data?.message || FALLBACK_ERROR), { errors: data?.errors || {} });
    return data;
  } catch (err) {
    if (err.name === "CanceledError" || !err.isAxiosError) throw err;
    const body = err.response?.data;
    throw Object.assign(new Error(body?.message || err.message || FALLBACK_ERROR), { errors: body?.errors || {} });
  }
};

// Same as send, but returns only the "data" object.
const request = (config) => send(config).then((body) => body.data);

// logged in admin's LMS access: { id, name, email, role: super_admin|sub_admin, permissions, can_manage }
export const getLmsMe = (signal) =>
  request({ method: "get", url: LMS_ADMIN + "me", signal });

// params: { tab: today|this_week|exceptions, search, page, per_page, date: YYYY-MM-DD }
export const getDashboard = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "dashboard", params, signal });

// params: { search, trainer_id, status: scheduled|moved|cancelled, from_date, to_date, page, per_page }
export const getSessions = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "sessions", params, signal });

// { title, batch, session_date, start_time, end_time, venue, room, trainer_id, student_ids, notify }
export const createSession = (payload) =>
  request({ method: "post", url: LMS_ADMIN + "sessions", data: payload });

// one class with its students, for the edit form
export const getSession = (id, signal) =>
  request({ method: "get", url: LMS_ADMIN + `sessions/${id}`, signal });

// send only the changed fields; student_ids replaces the full list.
// Changing date, time or room marks the class "moved" and tells students (notify_students, default true).
// Resolves to { message, data }.
export const updateSession = (id, payload) =>
  send({ method: "put", url: LMS_ADMIN + `sessions/${id}`, data: payload });

// { status_note } is optional (default "Cancelled by ops"). Emails everyone assigned. Resolves to { message, data }.
export const cancelSession = (id, payload = {}) =>
  send({ method: "post", url: LMS_ADMIN + `sessions/${id}/cancel`, data: payload });
// params: { search } (first 50 matches) or { batch } (every student of that batch)
export const getStudents = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "students", params, signal });

// Attendance screen cards, one day: { date, totals, sessions, students, trainers }
// params: { date: YYYY-MM-DD (default today) }
export const getMonitorOverview = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "monitor/overview", params, signal });

// "All students" screen: { cards, students, pagination }
// params: { tab: all|below_threshold|inactive, search (name, phone, email), page, per_page }
export const getMonitorStudents = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "monitor/students", params, signal });

// one student: { student, attendance, by_month, records, leaves }
export const getMonitorStudent = (id, signal) =>
  request({ method: "get", url: LMS_ADMIN + `monitor/students/${id}`, signal });

// "All trainers" screen, one day: { date, from_date, to_date, cards, trainers, pagination }
// params: { tab: all|punched_in|not_punched_in|unconfirmed, search (name, phone, email, batch), date (default today), page, per_page }
// Each row also has totals over from_date + to_date (default last 30 days).
export const getMonitorTrainers = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "monitor/trainers", params, signal });

// Student leave: { pending_count, counts, leaves, pagination }
// params: { status: pending|approved|rejected|cancelled, search (student name, phone), page, per_page }
export const getLeaves = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "leaves", params, signal });

// approve marks every class in the range as "leave" for the student. note is optional. Resolves to { message, data }.
export const approveLeave = (id, note) =>
  send({ method: "post", url: LMS_ADMIN + `leaves/${id}/approve`, data: { note: note || undefined } });

// note is optional (shown to the student). Resolves to { message, data }.
export const rejectLeave = (id, note) =>
  send({ method: "post", url: LMS_ADMIN + `leaves/${id}/reject`, data: { note: note || undefined } });

// Trainer requests to cancel a class: { pending_count, counts, requests, pagination }
// params: { status: pending|approved|rejected|withdrawn, search (class title, batch, trainer name), page, per_page }
export const getCancellationRequests = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "cancellation-requests", params, signal });

// cancels the class and emails + app-messages its students and the trainer.
// note is the reason they see (default "Cancelled by ops"). Resolves to { message, data }.
export const approveCancellationRequest = (id, note) =>
  send({ method: "post", url: LMS_ADMIN + `cancellation-requests/${id}/approve`, data: { note: note || undefined } });

// the class stays on, only the trainer is told. note is optional. Resolves to { message, data }.
export const rejectCancellationRequest = (id, note) =>
  send({ method: "post", url: LMS_ADMIN + `cancellation-requests/${id}/reject`, data: { note: note || undefined } });

// every trainer, for pickers (the list API pages by 20)
export const getTrainers = () => getMonitorTrainers({ per_page: 1000 }).then((data) => data.trainers);

// Broadcasts
// audiences: session_students, session_trainer, batch, batch_trainers, all_trainers, all_students
// (the session ones and batch need session_id or batch)

// template chips: { templates: [{ key, label, category, audiences, subject, message }], placeholders }
// params: { session_id } (optional, fills the class name, date and venue into the text)
export const getBroadcastTemplates = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "broadcasts/templates", params, signal });

// counts per checkbox + total people (each counted once): { session, batch, options, selected, total }
// params: { session_id, batch, audiences: [] }
export const getBroadcastRecipients = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "broadcasts/recipients", params, signal });

// { broadcasts, pagination }. params: { status: sent|draft (default sent), page, per_page (1-100) }
export const getBroadcasts = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "broadcasts", params, signal });

// one broadcast (or draft) with its delivery counts
export const getBroadcast = (id, signal) =>
  request({ method: "get", url: LMS_ADMIN + `broadcasts/${id}`, signal });

// { template, audiences, session_id, batch, category, subject, message, send_email, draft_id }
// draft_id sends a saved draft. Resolves to { message, data }.
export const sendBroadcast = (payload) =>
  send({ method: "post", url: LMS_ADMIN + "broadcasts", data: payload });

// same payload as sendBroadcast, every field optional. Pass id to save an existing draft again.
// Resolves to { message, data } (data.id is the draft id).
export const saveBroadcastDraft = (id, payload) =>
  id
    ? send({ method: "put", url: LMS_ADMIN + `broadcasts/drafts/${id}`, data: payload })
    : send({ method: "post", url: LMS_ADMIN + "broadcasts/drafts", data: payload });

// Resolves to { message, data }.
export const deleteBroadcastDraft = (id) =>
  send({ method: "delete", url: LMS_ADMIN + `broadcasts/drafts/${id}` });

// Attendance (the /staff APIs, shared by admins and trainers)

// { sessions, pagination }. params: { date (default today) or from_date + to_date, search (title, batch, trainer), page, per_page }
export const getStaffSessions = (params, signal) =>
  request({ method: "get", url: LMS_STAFF + "sessions", params, signal });

// { session, summary, students } for one class
export const getSessionAttendance = (id, signal) =>
  request({ method: "get", url: LMS_STAFF + `sessions/${id}/attendance`, signal });

// students: [{ user_id, status: present|absent }]. Resolves to { message, data }.
export const markAttendance = (sessionId, students) =>
  send({ method: "post", url: LMS_STAFF + `sessions/${sessionId}/attendance`, data: { students } });

// a student's own selfie check-in: action confirm|reject. Resolves to { message, data }.
export const confirmCheckin = (attendanceId, action) =>
  send({ method: "post", url: LMS_STAFF + `attendance/${attendanceId}/confirm`, data: { action } });

// confirms every pending check-in of the class. Resolves to { message, data }.
export const confirmAllCheckins = (sessionId) =>
  send({ method: "post", url: LMS_STAFF + `sessions/${sessionId}/attendance/confirm-all` });

// Sub admins (super admin only): sections given to a user one by one
// user: { id, name, email, phone_number, status, permissions, role_permissions (locked, from a role), roles }

// { users, pagination }. params: { search (name, email, phone), page, per_page }
export const getSubAdmins = (params, signal) =>
  request({ method: "get", url: LMS_ADMIN + "sub-admins", params, signal });

// user picker for "Add sub admin" (super admins left out), max 20. Needs 2+ letters, else [].
export const searchSubAdminUsers = (search, signal) =>
  request({ method: "get", url: LMS_ADMIN + "sub-admins/users", params: { search }, signal });

// replaces the user's sections, [] takes them all away. Resolves to { message, data: user }.
export const updateSubAdmin = (id, permissions) =>
  send({ method: "put", url: LMS_ADMIN + `sub-admins/${id}`, data: { permissions } });

// Resolves to { message, data: user }.
export const removeSubAdmin = (id) =>
  send({ method: "delete", url: LMS_ADMIN + `sub-admins/${id}` });
